/**
 * Стан незбереженого подання на сервері (views.md, розд. 6): чернетку, яка не йде в адресу (умова
 * на поле `personal` / `sensitive`, задовгий `?d=`, SEC-110), екран кладе на сервер, а в адресі
 * лишається `/l/<подання>?state=<ключ>` чи `/b/<подання>?state=<ключ>`. Читає стан лише автор: колега за таким посиланням
 * побачить «посилання застаріло» і чисте подання.
 *
 * Модуль — у першому екрані (його читає `loader` списку): без UI.
 */
import type { ViewState, ViewStateCreated, ViewStateDraft } from '@clientline/contracts';
import { parseFilterTree } from '@clientline/contracts/filter-eval';
import { type QueryClient, queryOptions } from '@tanstack/react-query';
import { api, retryRead } from './api.ts';
import { SORT_VALUE } from './list-context.ts';
import { queryPolicy } from './query-policy.ts';
import type { DecodedDraft, ViewDraft } from './view-draft.ts';

export const viewStateKey = (id: string) => ['p', 'view-state', id] as const;

/**
 * Стан за ключем. Чернетка під ключем незмінна (повтор тієї самої чернетки дає той самий ключ), тож
 * кеш — на сесію вкладки. «Недоступне» — ні: це права автора на мить читання, а їх можуть
 * повернути, тож воно перечитується, як свіже. `404` (чужий, прострочений, невідомий) не повторюємо.
 */
export const viewStateQuery = (id: string) =>
  queryOptions({
    queryKey: viewStateKey(id),
    queryFn: ({ signal }) =>
      api<ViewState>('GET', `/api/v1/views/state/${id}`, { ...(signal ? { signal } : {}) }),
    ...queryPolicy.stable,
    staleTime: (query) =>
      query.state.data && 'unavailable' in query.state.data
        ? queryPolicy.fresh.staleTime
        : queryPolicy.stable.staleTime,
    retry: retryRead,
  });

/**
 * Зберегти чернетку над поданням; ключ — у кеш разом із чернеткою, тож перехід на `?state=` не
 * робить другого запиту.
 */
export async function saveViewState(
  client: QueryClient,
  view: { id: string; entity: string },
  draft: ViewDraft,
): Promise<string> {
  const body: ViewStateDraft = {
    ...(draft.sort ? { sort: draft.sort } : {}),
    ...(draft.filter !== undefined ? { filter: draft.filter } : {}),
    ...(draft.q ? { q: draft.q } : {}),
  };
  const created = await api<ViewStateCreated>('POST', '/api/v1/views/state', {
    body: { viewId: view.id, draft: body },
  });
  const state: ViewState = {
    id: created.id,
    viewId: view.id,
    entity: view.entity,
    draft: body,
    expiresAt: created.expiresAt,
  };
  client.setQueryData(viewStateKey(created.id), state);
  return created.id;
}

/**
 * Чернетка зі стану для подання з адреси. Стан іншого подання, «недоступне» (права автора
 * звузились), збій читання чи чернетка не тієї форми — зіпсоване посилання, як зіпсований `d`.
 */
export function stateDraft(state: ViewState | undefined, viewId: string | undefined): DecodedDraft {
  if (!state || 'unavailable' in state || state.viewId !== viewId) return { ok: false };
  const draft: ViewDraft = {};
  const { sort, filter, q } = state.draft;
  if (sort) {
    if (!SORT_VALUE.test(`${sort.field}:${sort.dir}`)) return { ok: false };
    draft.sort = { field: sort.field, dir: sort.dir };
  }
  if (filter === null) draft.filter = null;
  else if (filter !== undefined) {
    const parsed = parseFilterTree(filter);
    if (!parsed.ok) return { ok: false };
    draft.filter = parsed.tree;
  }
  if (q) draft.q = q;
  return { ok: true, draft };
}
